import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Button, Checkbox } from '@atlas/ui-primitives'
import type { FieldMappingListRow } from '@/services/workfront-mappings.service'
import { humanize } from '../../components/StatusBadges'
import { ListState, type ListStateProps } from './ListState'
import { RowStatusBadge } from './MappingBadges'

export interface SelectableRows {
	/** Keys of the rows chosen for a bulk action. */
	selected: ReadonlySet<string>
	onToggle: (key: string) => void
}

export interface FieldsTableProps extends SelectableRows, Pick<ListStateProps, 'isLoading' | 'isError' | 'onRetry'> {
	rows: FieldMappingListRow[]
	/** None listed because the filters exclude every row, rather than none existing. */
	filtered?: boolean
	onEdit: (row: FieldMappingListRow) => void
}

/** The field a row reads: its current label, and the ID or name underneath. */
export const fieldSourceName = (row: FieldMappingListRow) =>
	row.sourceLabel ?? row.row.sourceParameterId ?? row.row.sourceField ?? '—'

function FieldRowView({
	row,
	selected,
	onToggle,
	onEdit,
}: Omit<FieldsTableProps, 'rows'> & { row: FieldMappingListRow }) {
	const name = fieldSourceName(row)
	const target = row.target
	return (
		<TableRow>
			<TableCell>
				<Checkbox
					aria-label={`Select ${name} → ${row.row.targetPath}`}
					isSelected={selected.has(row.key)}
					onChange={() => onToggle(row.key)}
				/>
			</TableCell>
			<TableCell>
				<span className="block font-medium">{name}</span>
				<span className="text-ds-xs text-ds-text-muted">{row.row.sourceParameterId ?? row.row.sourceKind}</span>
			</TableCell>
			<TableCell>{humanize(row.row.sourceRole)}</TableCell>
			<TableCell>
				<span className="block">{target?.label ?? row.row.targetPath}</span>
				<span className="text-ds-xs text-ds-text-muted">
					{row.row.targetPath}
					{target ? ` · ${target.level} · ${target.type}` : ''}
					{target?.required ? ' · required' : ''}
				</span>
			</TableCell>
			<TableCell>{humanize(row.row.rule)}</TableCell>
			<TableCell>{row.fallbackLabel ?? row.row.fallbackParameterId ?? '—'}</TableCell>
			<TableCell>
				<RowStatusBadge status={row.status} enabled={row.row.enabled} />
			</TableCell>
			<TableCell>
				<Button
					size="sm"
					variant="ghost"
					aria-label={`Edit ${name} → ${row.row.targetPath}`}
					onPress={() => onEdit(row)}
				>
					Edit
				</Button>
			</TableCell>
		</TableRow>
	)
}

/** The field mappings: what each reads, what it writes, by which rule, and where it stands against the draft. */
export function FieldsTable({ rows, isLoading, isError, onRetry, filtered, ...rest }: FieldsTableProps) {
	return (
		<ListState
			noun="field mappings"
			isLoading={isLoading}
			isError={isError}
			onRetry={onRetry}
			isEmpty={rows.length === 0}
			emptyTitle={filtered ? 'No field mappings match' : 'No field mappings yet'}
			emptyDescription={filtered ? 'Change the filters to see more.' : 'Import the seed, or add a row.'}
		>
			<Table aria-label="Field mappings">
				<TableHeader>
					<TableRow>
						{['', 'Workfront field', 'Role', 'ATLAS target', 'Rule', 'Fallback', 'Status', ''].map((column, index) => (
							<TableHead key={`${column}-${index}`}>{column}</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((row) => (
						<FieldRowView key={row.key} row={row} {...rest} />
					))}
				</TableBody>
			</Table>
		</ListState>
	)
}
